import {Fragment, useState} from 'react';
import {isBrowser} from '@shopify/hydrogen';
import {useNavigate} from '@shopify/hydrogen/client';
import {Dialog, Transition} from '@headlessui/react';
import {AccessCodeFormContent} from '~/components/events/AccessCodeFormContent';
import {InformationCircleIcon} from '@heroicons/react/20/solid';

export function AccessCodeModal() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    general?: string;
    access_code?: string;
  }>({
    general: undefined,
    access_code: undefined,
  });

  if (isBrowser()) {
    window.addEventListener('access-code.toggle', () => setOpen(!open), false);
  }

  const handleLostCodeClick = async () => {
    setOpen(false);

    navigate('/pages/contact');
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setSubmitting(true);
    setErrors({
      general: undefined,
      access_code: undefined,
    });

    try {
      const target = e.target as typeof e.target & {
        access_code: {value?: string};
      };
      const accessCode = target.access_code.value;

      const accessResponse = await fetch('/api/accessCode', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({access_code: accessCode}),
      });

      const data = await accessResponse.json();

      switch (accessResponse.status) {
        case 200: {
          setSuccess(true);
          navigate(`/events/${data.event.handle}`);

          setTimeout(() => {
            setOpen(false);
          }, 300);

          break;
        }
        case 404: {
          setErrors({
            access_code: data.message,
            general: undefined,
          });

          break;
        }

        default: {
          setErrors({
            access_code: undefined,
            general: 'An error occurred',
          });

          break;
        }
      }
    } catch (error: any) {
      setErrors({
        access_code: undefined,
        general: 'An error occurred',
      });
    }

    setSubmitting(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full w-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <form
                      className="space-y-6"
                      onSubmit={(e) => handleSubmit(e)}
                      autoComplete="off"
                    >
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Enter access code
                      </Dialog.Title>

                      <AccessCodeFormContent
                        submitting={submitting}
                        error={errors.access_code}
                      />
                    </form>
                  </div>

                  {/* Lost code alert */}
                  <div className="mt-4 rounded-md bg-blue-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon
                          className="h-5 w-5 text-blue-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3 flex-1 md:flex md:justify-between">
                        <p className="text-sm text-blue-700">
                          Lost your access code?
                        </p>
                        <p className="mt-3 text-sm md:mt-0 md:ml-6">
                          <a
                            className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                            href="/pages/contact"
                            onClick={handleLostCodeClick}
                          >
                            Contact us
                            <span aria-hidden="true"> &rarr;</span>
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
